import { useId } from 'react'

export interface TrendPoint {
  /** x-axis label (shown for the first, middle and last points). */
  label: string
  /** `null` = still in the future: no line drawn there. */
  value: number | null
}

/**
 * Minimal trend line, ported from the mockup's `components/TrendChart.tsx`: shape only, no y-axis numbers. The
 * x-axis spans the whole period; the line stops at today (UserFlows C-01).
 */
export function TrendChart({ points, height = 120 }: { points: TrendPoint[]; height?: number }) {
  const id = useId()
  const W = 600
  const pad = 6
  const max = Math.max(1, ...points.map((p) => p.value ?? 0))
  const step = points.length > 1 ? W / (points.length - 1) : W
  const xy = points
    .map((p, i) =>
      p.value === null
        ? null
        : ([points.length > 1 ? i * step : W / 2, pad + (1 - p.value / max) * (height - pad * 2)] as const),
    )
    .filter((p): p is readonly [number, number] => p !== null)
  const line = xy.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
  const first = xy[0]
  const last = xy[xy.length - 1]
  const area =
    first && last ? `${line} L${last[0].toFixed(1)},${String(height)} L${first[0].toFixed(1)},${String(height)} Z` : ''
  const ticks =
    points.length > 2 ? [0, Math.floor((points.length - 1) / 2), points.length - 1] : points.map((_, i) => i)

  return (
    <div className="trend" data-points={xy.length}>
      <svg viewBox={`0 0 ${String(W)} ${String(height)}`} preserveAspectRatio="none" style={{ height }} aria-hidden>
        <defs>
          <linearGradient id={`g${id}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--blue)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="var(--blue)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={f}
            x1="0"
            x2={W}
            y1={height * f}
            y2={height * f}
            className="trend-grid"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {xy.length > 1 && <path d={area} fill={`url(#g${id})`} />}
        {xy.length > 1 && <path d={line} className="trend-line" vectorEffect="non-scaling-stroke" />}
      </svg>
      {last && <span className="trend-dot" style={{ left: `${String((last[0] / W) * 100)}%`, top: last[1] }} />}
      <div className="trend-axis" aria-hidden>
        {ticks.map((i) => (
          <span key={i} style={{ left: `${String(points.length > 1 ? (i / (points.length - 1)) * 100 : 50)}%` }}>
            {points[i]?.label}
          </span>
        ))}
      </div>
    </div>
  )
}
